import { useCallback, useEffect, useState } from 'react';
import { EntityModal } from './EntityModal';
import { extractApiErrorMessage } from '../../utils/apiError';

export interface QuickEditField {
  key: string;
  label: string;
  type?: 'text' | 'textarea' | 'email' | 'tel' | 'url';
  placeholder?: string;
  required?: boolean;
  /** Render as read-only text (context the user can see but not change). */
  readOnly?: boolean;
}

export interface QuickEditModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Small context line under the title (e.g. the item's category). */
  subtitle?: string;
  fields: QuickEditField[];
  initialValues: Record<string, string>;
  /** Resolves on success (modal closes); rejection keeps it open and shows the error. */
  onSave: (values: Record<string, string>) => Promise<void>;
  saving?: boolean;
  /** Entity still loading — renders a skeleton body. */
  loading?: boolean;
}

/**
 * Small generic view/edit modal for a just-linked entity (vendor, catalog
 * item, ...). Deliberately does NOT close on backdrop click — it only closes
 * via the X button, the Close button, or a successful save.
 */
export function QuickEditModal({
  open,
  onClose,
  title,
  subtitle,
  fields,
  initialValues,
  onSave,
  saving = false,
  loading = false,
}: QuickEditModalProps) {
  const [values, setValues] = useState<Record<string, string>>(initialValues);
  const [error, setError] = useState('');

  // Re-seed the form whenever the modal opens or the entity finishes loading
  // (serialized so a referentially-new-but-equal object doesn't wipe edits).
  const initialValuesKey = JSON.stringify(initialValues);
  useEffect(() => {
    if (open) {
      setValues(JSON.parse(initialValuesKey) as Record<string, string>);
      setError('');
    }
  }, [open, initialValuesKey]);

  const dirty = fields.some(
    (f) => !f.readOnly && (values[f.key] ?? '') !== (initialValues[f.key] ?? ''),
  );
  const missingRequired = fields.some((f) => f.required && !(values[f.key] ?? '').trim());

  const handleSave = useCallback(async () => {
    setError('');
    try {
      await onSave(values);
      onClose();
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Failed to save changes'));
    }
  }, [values, onSave, onClose]);

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      presentation="centered"
      closeOnBackdrop={false}
      footer={
        <>
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
          >
            Close
          </button>
          <button
            onClick={handleSave}
            disabled={!dirty || missingRequired || saving || loading}
            className="px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </>
      }
    >
      {subtitle && <p className="text-xs text-gray-500 -mt-1 mb-3">{subtitle}</p>}

      {error && (
        <div className="mb-3 px-3 py-2 bg-red-50 text-red-700 text-sm rounded">{error}</div>
      )}

      {loading ? (
        <div className="space-y-3" data-testid="quick-edit-skeleton">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-9 bg-gray-100 rounded-md animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {fields.map((field) => (
            <div key={field.key}>
              <label
                htmlFor={`quick-edit-${field.key}`}
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {field.label}
                {field.required && ' *'}
              </label>
              {field.readOnly ? (
                <p className="text-sm text-gray-600 px-3 py-2 bg-gray-50 rounded-md">
                  {values[field.key] || '—'}
                </p>
              ) : field.type === 'textarea' ? (
                <textarea
                  id={`quick-edit-${field.key}`}
                  value={values[field.key] ?? ''}
                  onChange={(e) => setValues((v) => ({ ...v, [field.key]: e.target.value }))}
                  placeholder={field.placeholder}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                />
              ) : (
                <input
                  id={`quick-edit-${field.key}`}
                  type={field.type ?? 'text'}
                  value={values[field.key] ?? ''}
                  onChange={(e) => setValues((v) => ({ ...v, [field.key]: e.target.value }))}
                  placeholder={field.placeholder}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              )}
            </div>
          ))}
        </div>
      )}
    </EntityModal>
  );
}
